/**
 * AI provider catalogue. The platform talks to four kinds of AI service —
 * speech-to-text, translation, text-to-speech and a language model — and each
 * can come from a different vendor, chosen in the admin panel. Everything the
 * rest of the platform needs to know about a vendor lives here; adding one
 * means adding an entry here and an adapter on the server.
 *
 * Language support was taken from each vendor's documentation in October 2026.
 * Voices change: the admin panel's "Test" checks every configured voice still
 * exists before you rely on it.
 */
import { z } from "zod";
import { LANGUAGES, type ProviderStatus } from "./languages.js";

export const AI_CAPABILITIES = ["stt", "translate", "tts", "llm"] as const;
export type AiCapability = (typeof AI_CAPABILITIES)[number];

export const CAPABILITY_LABEL: Record<AiCapability, string> = {
  stt: "Speech to text",
  translate: "Translation",
  tts: "Voice (text to speech)",
  llm: "Analysis and assistant (language model)",
};

export const CAPABILITY_SHORT: Record<AiCapability, string> = {
  stt: "Speech to text",
  translate: "Translation",
  tts: "Voice",
  llm: "Analysis model",
};

export const AI_PROVIDER_IDS = ["google", "aws", "azure", "anthropic", "openai", "mock"] as const;
export type AiProviderId = (typeof AI_PROVIDER_IDS)[number];

export interface CredentialField {
  key: string;
  label: string;
  /** Secret values are write-only: never shown again once saved. */
  secret: boolean;
  optional?: boolean;
  placeholder?: string;
  help?: string;
}

export interface AiProviderDef {
  id: AiProviderId;
  name: string;
  /** Legal entity that processes the data (for the sub-processor list). */
  processor: string;
  capabilities: AiCapability[];
  credentials: CredentialField[];
  /** Suggested language-model identifiers; editable in the admin panel. */
  llmDefaults?: { analysis: string; live: string; help: string };
  /** Glossary (client terminology) enforced inside the translation service. */
  glossary: boolean;
  /** Recognition can be biased with the engagement's terms. */
  phraseHints: boolean;
  note: string;
  /** Only available outside production (demo and tests). */
  devOnly?: boolean;
}

export const AI_PROVIDERS: Record<AiProviderId, AiProviderDef> = {
  google: {
    id: "google",
    name: "Google Cloud",
    processor: "Google LLC (Google Cloud)",
    capabilities: ["stt", "translate", "tts", "llm"],
    credentials: [],
    llmDefaults: {
      analysis: "claude-opus-5-5",
      live: "claude-haiku-5-5",
      help: "Gemini (model IDs starting gemini-, e.g. gemini-3.1-pro-preview and gemini-3.8-flash) works at once on a new project. Claude on Vertex AI (claude-…) needs the model enabled in the Model Garden and a quota from Google.",
    },
    glossary: true,
    phraseHints: true,
    note: "Uses the platform's own Google Cloud identity; no keys to manage. Chirp 3 speech, Translation LLM, Chirp 3 HD voices, and Gemini or Claude on Vertex AI.",
  },
  aws: {
    id: "aws",
    name: "Amazon Web Services",
    processor: "Amazon Web Services, Inc.",
    capabilities: ["stt", "translate", "tts", "llm"],
    credentials: [
      { key: "region", label: "AWS region", secret: false, placeholder: "us-east-1", help: "Region for Transcribe, Translate, Polly, Comprehend and Bedrock." },
      { key: "accessKeyId", label: "Access key ID", secret: true },
      { key: "secretAccessKey", label: "Secret access key", secret: true },
    ],
    llmDefaults: {
      analysis: "global.anthropic.claude-opus-5-5",
      live: "global.anthropic.claude-haiku-5-5",
      help: "Claude in Amazon Bedrock. 'global.' routes across regions; use 'us.' or a regional ID for data residency (10% premium). Enable model access in the AWS console.",
    },
    glossary: true,
    phraseHints: false,
    note: "Amazon Transcribe (streaming), Translate (custom terminology), Polly neural voices, Comprehend for language detection, Claude in Amazon Bedrock. Use an IAM user limited to these services.",
  },
  azure: {
    id: "azure",
    name: "Microsoft Azure",
    processor: "Microsoft Corporation (Azure)",
    capabilities: ["stt", "translate", "tts", "llm"],
    credentials: [
      { key: "speechKey", label: "Speech resource key", secret: true, optional: true },
      { key: "speechRegion", label: "Speech region", secret: false, optional: true, placeholder: "centralindia" },
      { key: "translatorKey", label: "Translator key", secret: true, optional: true },
      { key: "translatorRegion", label: "Translator region", secret: false, optional: true, placeholder: "centralindia" },
      { key: "openaiEndpoint", label: "Azure OpenAI endpoint", secret: false, optional: true, placeholder: "https://<resource>.openai.azure.com" },
      { key: "openaiKey", label: "Azure OpenAI key", secret: true, optional: true },
    ],
    llmDefaults: {
      analysis: "",
      live: "",
      help: "Azure OpenAI deployment names you created (not model names).",
    },
    glossary: false,
    phraseHints: true,
    note: "Azure AI Speech (real-time recognition, neural voices), Translator, Azure OpenAI. Strong Marathi and Hindi coverage.",
  },
  anthropic: {
    id: "anthropic",
    name: "Anthropic (direct)",
    processor: "Anthropic, PBC",
    capabilities: ["llm"],
    credentials: [{ key: "apiKey", label: "API key", secret: true }],
    llmDefaults: { analysis: "claude-opus-5-5", live: "claude-haiku-5-5", help: "Claude API model IDs." },
    glossary: false,
    phraseHints: false,
    note: "Claude through Anthropic's own API.",
  },
  openai: {
    id: "openai",
    name: "OpenAI (direct)",
    processor: "OpenAI, L.L.C.",
    capabilities: ["llm"],
    credentials: [{ key: "apiKey", label: "API key", secret: true }],
    llmDefaults: { analysis: "gpt-6-astra", live: "gpt-6-luna", help: "OpenAI API model IDs." },
    glossary: false,
    phraseHints: false,
    note: "OpenAI models through OpenAI's own API. Analysis prompts are written for Claude; review output quality before switching clients over.",
  },
  mock: {
    id: "mock",
    name: "Simulated (demo)",
    processor: "None — runs inside the platform",
    capabilities: ["stt", "translate", "tts", "llm"],
    credentials: [],
    llmDefaults: { analysis: "mock-analysis", live: "mock-live", help: "Scripted output for demos and tests." },
    glossary: false,
    phraseHints: false,
    note: "Scripted speech and analysis for demos and tests. Never available in production.",
    devOnly: true,
  },
};

// ------------------------------------------------------------------ language support

export interface SttSupport {
  status: ProviderStatus;
  code: string;
  fallbackCode?: string;
  note?: string;
}
export interface TtsSupport {
  status: ProviderStatus;
  voice: string;
  voiceLocale: string;
  note?: string;
}
export interface TranslateSupport {
  status: ProviderStatus;
  code: string;
}
export interface LanguageSupport {
  stt: SttSupport;
  tts: TtsSupport;
  translate: TranslateSupport;
}

const no = { status: "unsupported" as const };

const AWS: Record<string, LanguageSupport> = {
  "en-US": { stt: { status: "ga", code: "en-US" }, tts: { status: "ga", voice: "Matthew", voiceLocale: "en-US" }, translate: { status: "ga", code: "en" } },
  "en-IN": { stt: { status: "ga", code: "en-IN" }, tts: { status: "ga", voice: "Kajal", voiceLocale: "en-IN" }, translate: { status: "ga", code: "en" } },
  "hi-IN": { stt: { status: "ga", code: "hi-IN" }, tts: { status: "ga", voice: "Kajal", voiceLocale: "hi-IN" }, translate: { status: "ga", code: "hi" } },
  "mr-IN": {
    stt: { status: "ga", code: "mr-IN", note: "Streaming not offered in a few AWS regions (e.g. Tokyo)." },
    tts: { ...no, voice: "", voiceLocale: "mr-IN", note: "Amazon Polly has no Marathi voice; set a fallback voice provider." },
    translate: { status: "ga", code: "mr" },
  },
  "es-MX": {
    stt: { status: "ga", code: "es-MX", note: "Streaming not offered in a few AWS regions (e.g. Tokyo)." },
    tts: { status: "ga", voice: "Mia", voiceLocale: "es-MX" },
    translate: { status: "ga", code: "es-MX" },
  },
  "es-US": { stt: { status: "ga", code: "es-US" }, tts: { status: "ga", voice: "Lupe", voiceLocale: "es-US" }, translate: { status: "ga", code: "es" } },
  "pt-BR": { stt: { status: "ga", code: "pt-BR" }, tts: { status: "ga", voice: "Camila", voiceLocale: "pt-BR" }, translate: { status: "ga", code: "pt" } },
  "fr-FR": { stt: { status: "ga", code: "fr-FR" }, tts: { status: "ga", voice: "Lea", voiceLocale: "fr-FR" }, translate: { status: "ga", code: "fr" } },
};

const AZURE: Record<string, LanguageSupport> = {
  "en-US": { stt: { status: "ga", code: "en-US" }, tts: { status: "ga", voice: "en-US-GuyNeural", voiceLocale: "en-US" }, translate: { status: "ga", code: "en" } },
  "en-IN": { stt: { status: "ga", code: "en-IN" }, tts: { status: "ga", voice: "en-IN-PrabhatNeural", voiceLocale: "en-IN" }, translate: { status: "ga", code: "en" } },
  "hi-IN": { stt: { status: "ga", code: "hi-IN" }, tts: { status: "ga", voice: "hi-IN-MadhurNeural", voiceLocale: "hi-IN" }, translate: { status: "ga", code: "hi" } },
  "mr-IN": {
    stt: { status: "ga", code: "mr-IN", note: "Phrase hints are not applied for Marathi." },
    tts: { status: "ga", voice: "mr-IN-ManoharNeural", voiceLocale: "mr-IN" },
    translate: { status: "ga", code: "mr" },
  },
  "es-MX": { stt: { status: "ga", code: "es-MX" }, tts: { status: "ga", voice: "es-MX-DaliaNeural", voiceLocale: "es-MX" }, translate: { status: "ga", code: "es" } },
  "es-US": { stt: { status: "ga", code: "es-US" }, tts: { status: "ga", voice: "es-US-PalomaNeural", voiceLocale: "es-US" }, translate: { status: "ga", code: "es" } },
  "pt-BR": { stt: { status: "ga", code: "pt-BR" }, tts: { status: "ga", voice: "pt-BR-FranciscaNeural", voiceLocale: "pt-BR" }, translate: { status: "ga", code: "pt" } },
  "fr-FR": { stt: { status: "ga", code: "fr-FR" }, tts: { status: "ga", voice: "fr-FR-DeniseNeural", voiceLocale: "fr-FR" }, translate: { status: "ga", code: "fr" } },
};

function googleSupport(code: string): LanguageSupport | null {
  const l = LANGUAGES.find((x) => x.code === code);
  if (!l) return null;
  return {
    stt: { status: l.stt.status, code: l.stt.code, ...(l.stt.fallbackCode ? { fallbackCode: l.stt.fallbackCode } : {}) },
    tts: { status: l.tts.status, voice: l.tts.voice, voiceLocale: l.tts.voiceLocale, ...(l.tts.note ? { note: l.tts.note } : {}) },
    translate: { status: "ga", code: l.base },
  };
}

function mockSupport(code: string): LanguageSupport | null {
  const l = LANGUAGES.find((x) => x.code === code);
  if (!l) return null;
  return { stt: { status: "ga", code }, tts: { status: "ga", voice: "tone", voiceLocale: code }, translate: { status: "ga", code: l.base } };
}

/** A bare base code ("es") stands for the first catalogued language of that family. */
function resolveCode(code: string): string {
  if (code.includes("-")) return code;
  return LANGUAGES.find((l) => l.base === code.toLowerCase())?.code ?? code;
}

/** How one provider handles one language; null when the provider does not offer the capability. */
export function languageSupport(provider: AiProviderId, code: string): LanguageSupport | null {
  const langCode = resolveCode(code);
  switch (provider) {
    case "google":
      return googleSupport(langCode);
    case "aws":
      return AWS[langCode] ?? null;
    case "azure":
      return AZURE[langCode] ?? null;
    case "mock":
      return mockSupport(langCode);
    default:
      return null;
  }
}

export function supports(provider: AiProviderId, capability: Exclude<AiCapability, "llm">, langCode: string): boolean {
  if (!AI_PROVIDERS[provider].capabilities.includes(capability)) return false;
  const s = languageSupport(provider, langCode);
  return Boolean(s && s[capability].status !== "unsupported");
}

// ------------------------------------------------------------------ settings

const ProviderId = z.enum(AI_PROVIDER_IDS);

export const CapabilityChoice = z.object({
  provider: ProviderId,
  /** Used for a language the primary provider does not support. */
  fallback: ProviderId.nullable().default(null),
});
export type CapabilityChoice = z.infer<typeof CapabilityChoice>;

export const LlmChoice = z.object({
  provider: ProviderId,
  analysisModel: z.string().trim().min(1).max(200),
  liveModel: z.string().trim().min(1).max(200),
});
export type LlmChoice = z.infer<typeof LlmChoice>;

export const AiSettings = z.object({
  stt: CapabilityChoice,
  translate: CapabilityChoice,
  tts: CapabilityChoice,
  llm: LlmChoice,
});
export type AiSettings = z.infer<typeof AiSettings>;

/** A client's override: any capability left out follows the platform default. */
export const AiOverride = z.object({
  stt: CapabilityChoice.optional(),
  translate: CapabilityChoice.optional(),
  tts: CapabilityChoice.optional(),
  llm: LlmChoice.optional(),
});
export type AiOverride = z.infer<typeof AiOverride>;

export const GOOGLE_DEFAULT_AI_SETTINGS: AiSettings = {
  stt: { provider: "google", fallback: null },
  translate: { provider: "google", fallback: null },
  tts: { provider: "google", fallback: null },
  llm: { provider: "google", analysisModel: "claude-opus-5-5", liveModel: "claude-haiku-5-5" },
};

export const MOCK_AI_SETTINGS: AiSettings = {
  stt: { provider: "mock", fallback: null },
  translate: { provider: "mock", fallback: null },
  tts: { provider: "mock", fallback: null },
  llm: { provider: "mock", analysisModel: "mock-analysis", liveModel: "mock-live" },
};

export function effectiveAiSettings(platform: AiSettings, override: AiOverride | null | undefined): AiSettings {
  return {
    stt: override?.stt ?? platform.stt,
    translate: override?.translate ?? platform.translate,
    tts: override?.tts ?? platform.tts,
    llm: override?.llm ?? platform.llm,
  };
}

/** Which provider serves a language for a capability: the primary if it can, else the fallback. */
export function routeFor(choice: CapabilityChoice, capability: Exclude<AiCapability, "llm">, langCode: string): AiProviderId | null {
  if (supports(choice.provider, capability, langCode)) return choice.provider;
  if (choice.fallback && supports(choice.fallback, capability, langCode)) return choice.fallback;
  return null;
}

/** Translation needs one provider that handles both languages. */
export function routeTranslation(choice: CapabilityChoice, from: string, to: string): AiProviderId | null {
  const ok = (p: AiProviderId) => supports(p, "translate", from) && supports(p, "translate", to);
  if (ok(choice.provider)) return choice.provider;
  if (choice.fallback && ok(choice.fallback)) return choice.fallback;
  return null;
}

export interface CoverageGap {
  capability: Exclude<AiCapability, "llm">;
  language: string;
}

/** Languages the chosen providers cannot serve, so the admin panel can warn before saving. */
export function coverageGaps(settings: AiSettings, languages: readonly string[] = LANGUAGES.map((l) => l.code)): CoverageGap[] {
  const gaps: CoverageGap[] = [];
  for (const language of languages) {
    for (const capability of ["stt", "translate", "tts"] as const) {
      if (!routeFor(settings[capability], capability, language)) gaps.push({ capability, language });
    }
  }
  return gaps;
}

/** Providers whose data processing a settings choice involves (for the sub-processor record). */
export function providersInUse(settings: AiSettings): AiProviderId[] {
  const set = new Set<AiProviderId>();
  for (const c of ["stt", "translate", "tts"] as const) {
    set.add(settings[c].provider);
    if (settings[c].fallback) set.add(settings[c].fallback!);
  }
  set.add(settings.llm.provider);
  return [...set];
}
